import { EVENTS } from "@archetype-themes/utils/events";

class ToggleMenu extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.isMobileNavOpen = false;
    this.openTrigger = this.querySelector('[aria-controls="MobileNav"]');

    if (!this.openTrigger) return;

    // Initialize ARIA attributes
    this.initAria();

    // Event listeners
    this.openTrigger.addEventListener("click", this.handleClick.bind(this), {
      signal: this.abortController.signal,
    });

    document.addEventListener(
      EVENTS.headerDrawerClosed,
      this.handleDrawerClosed.bind(this),
      {
        signal: this.abortController.signal,
      }
    );

    // Sync state with other components
    document.addEventListener(EVENTS.mobileNavOpen, () => {
      this.updateAria(true);
    });

    document.addEventListener(EVENTS.mobileNavClose, () => {
      this.updateAria(false);
    });
  }

  initAria() {
    // Set initial ARIA attributes
    this.openTrigger.setAttribute("aria-expanded", "false");
    this.openTrigger.setAttribute("aria-haspopup", "true");
    this.openTrigger.setAttribute(
      "aria-label",
      this.openTrigger.getAttribute("aria-label") || "Menu"
    );

    // Ensure controlled element exists
    const mobileNav = document.getElementById(
      this.openTrigger.getAttribute("aria-controls")
    );
    if (mobileNav) {
      mobileNav.setAttribute("aria-hidden", "true");
      mobileNav.setAttribute("aria-modal", "true");
      mobileNav.setAttribute("role", "dialog");
    }
  }

  handleClick(evt) {
    evt.preventDefault();
    this.isMobileNavOpen = !this.isMobileNavOpen;
    this.updateAria(this.isMobileNavOpen);

    const eventName = this.isMobileNavOpen
      ? EVENTS.mobileNavOpen
      : EVENTS.mobileNavClose;
    evt.target.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));

    this.openTrigger.classList.toggle("is-active", this.isMobileNavOpen);

    // Focus management for accessibility
    if (this.isMobileNavOpen) {
      const mobileNav = document.getElementById(
        this.openTrigger.getAttribute("aria-controls")
      );
      const firstFocusable = mobileNav.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }
  }

  updateAria(isOpen) {
    this.openTrigger.setAttribute("aria-expanded", String(isOpen));

    const mobileNav = document.getElementById(
      this.openTrigger.getAttribute("aria-controls")
    );
    if (mobileNav) {
      mobileNav.setAttribute("aria-hidden", String(!isOpen));
      mobileNav.inert = !isOpen;
    }
  }

  handleDrawerClosed() {
    this.isMobileNavOpen = false;
    this.updateAria(false);
    this.openTrigger.classList.remove("is-active");
  }

  disconnectedCallback() {
    this.abortController.abort();
  }
}

customElements.define("toggle-menu", ToggleMenu);
